import * as React from 'react';
import styled from '@independent-software/typeui/styles/Theme';
import { Map, ViewStateChangeEvent, MapLayerMouseEvent, Source, Layer, MapboxMap, Popup } from 'react-map-gl';
import bbox from '@turf/bbox';

import { App } from '../../App';
import { ISystem, SystemCollection } from './SystemCollection';
import { AuthContext } from '../../AuthContext';
import { MapControls } from '../../maps/MapControls/MapControls';
import { MarkerFillLayer } from './layers/MarkerFillLayer';
import { MarkerStrokeLayer } from './layers/MarkerStrokeLayer';
import { HullFillLayer } from './layers/HullFillLayer';
import { HullFillLayerHovered } from './layers/HullFillLayerHovered';
import { HullStrokeLayer } from './layers/HullStrokeLayer';
import { HullStrokeLayerHovered } from './layers/HullStrokeLayerHovered';
import { StationFillLayer } from './layers/StationFillLayer';
import { StationStrokeLayer } from './layers/StationStrokeLayer';
import { MarkerSymbolLayer } from './layers/MarkerSymbolLayer';

const MINZOOM = 1;       
const MAXZOOM = 17;
const MARKER_THRESHOLD = 6;

interface IProps {
  systemIDs: number[];
}

interface IHover {
  lat: number;
  lng: number;
  system: ISystem;
}

const ProjectMap = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const map = React.useRef(null as MapboxMap);
  const [viewState, setViewState] = React.useState({
    longitude: 0,
    latitude: 0, 
    zoom: 2,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 }    
  });
  const [interactiveLayerIds, setInteractiveLayerIds] = React.useState([]);
  const [systems, setSystems] = React.useState([] as ISystem[]);
  const [hover, setHover] = React.useState({ system: null } as IHover);

  React.useEffect(() => {
    loadSystems();
  }, [props.systemIDs]);

  const loadSystems = () => {
    // SystemCollection is specific for this map. It implements stations
    // retrieval and creating hull polygons.
    SystemCollection.getSystems(auth, props.systemIDs)
    .then((res: ISystem[]) => {
      setSystems(res);
      // Fit map to systems, but only if at least 1 system selected.
      if(props.systemIDs.length > 0 && map.current !== null) {
        map.current.fitBounds(SystemCollection.getBounds(res), {
          animate: false,
          padding: 100
        });
      }
    });
  }

  const handleLoad = (e: mapboxgl.MapboxEvent) => {
    map.current = e.target;
    setInteractiveLayerIds(["hull-fill-layer"]);
    loadSystems();
  }

  const handleMove = (e: ViewStateChangeEvent) => {
    setViewState(e.viewState);
  }

  const handleMouseMove = (e: MapLayerMouseEvent) => {
    // If source isn't yet loaded, MapGL throws an error:
    if(!map.current || !map.current.loaded) return;

    // Turn hovering off:
    if(e.features.length == 0) return;

    // If hovering over a feature, set hover to feature's id property.
    const featureID = e.features[0].properties.id;
    const system = systems.find(s => s.id === featureID);
    
    setHover({
      lat: e.lngLat.lat,
      lng: e.lngLat.lng,
      system: system
    });
  }  
  
  const handleMouseLeave = (e: MapLayerMouseEvent) => {
    setHover({ ...hover, system: null });
  }  

  const handleClick = (e: MapLayerMouseEvent) => {
    // If source isn't yet loaded, MapGL throws an error:
    if(!map.current || !map.current.loaded) return;

    if(e.features.length == 0) return;

    const box = bbox(e.features[0]);
    map.current.fitBounds([[box[0], box[1]], [box[2], box[3]]], {
      padding: 20
    });
  }

  return (
    <>
      <Map
        {...viewState}
        mapboxAccessToken={App.config.mapboxKey}
        style={{width: '100%', height: '100%'}}
        cursor={hover.system ? 'pointer' : 'auto'}
        logoPosition="bottom-left"  // Moves mapbox logo
        fadeDuration={0}          // Prevents fading in of cluster labels
        interactiveLayerIds={interactiveLayerIds}
        mapStyle="mapbox://styles/longlineenvironment/cl8x4af5v003n14ld74f6ugwd"
        minZoom={MINZOOM}
        maxZoom={MAXZOOM}
        doubleClickZoom={false}
        //Events
        onLoad={handleLoad}
        onMove={handleMove}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <MapControls {...viewState}/>

        <Source id="systems-centers" type="geojson" data={{type: 'FeatureCollection', features: systems.map(s => s.center)}}>
          <Layer {...MarkerFillLayer} filter={["<=", ["zoom"], MARKER_THRESHOLD]}/>
          <Layer {...MarkerStrokeLayer} filter={["<=", ["zoom"], MARKER_THRESHOLD]}/>
          <Layer {...MarkerSymbolLayer} filter={["<=", ["zoom"], MARKER_THRESHOLD]}/>
        </Source>

        <Source id="systems" type="geojson" data={{type: 'FeatureCollection', features: systems.map(s => s.hull)}}>
          <Layer {...HullFillLayer} filter={[">", ["zoom"], MARKER_THRESHOLD]}/>
          <Layer {...HullFillLayerHovered} filter={['==', ['get', 'id'], hover.system ? hover.system.id : null]}/>
          <Layer {...HullStrokeLayer} filter={[">", ["zoom"], MARKER_THRESHOLD]}/>
          <Layer {...HullStrokeLayerHovered} filter={['==', ['get', 'id'], hover.system ? hover.system.id : null]}/>
        </Source>

        {systems.map((system, index) => 
          <Source key={index} type="geojson" data={system.stations}>
            <Layer {...StationFillLayer} id={`station-fill-${index}`} filter={[">", ["zoom"], MARKER_THRESHOLD]}/>
            <Layer {...StationStrokeLayer} id={`station-stroke-${index}`} filter={[">", ["zoom"], MARKER_THRESHOLD]}/>
          </Source>
        )}      
        
        {hover.system !== null &&
          <Popup 
            longitude={hover.lng}
            latitude={hover.lat}
            offset={[0,-10]}
            closeButton={false}
            anchor='bottom'>
            <PopupTitle>{hover.system.name}</PopupTitle>
            <PopupBody>{hover.system.stations.features.length} stations</PopupBody>
          </Popup>
        }
      </Map>
    </>
  )
}

const PopupTitle = styled.div`
  font-weight: bold;
  text-align: center;
  font-size: 14px;
`

const PopupBody = styled.div`
  text-align: center;
`

export { ProjectMap }
